/* eslint-disable no-console */
import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import { useAccount, useSignMessage, useDisconnect } from 'wagmi';
import { SiweMessage } from 'siwe';

/**
 * Session data stored in localStorage
 */
interface AuthSession {
  address: string;
  chainId: number;
  signature: string;
  message: string;
  expiresAt: number; // Unix timestamp
  issuedAt: number;  // Unix timestamp
}

/**
 * Authentication state
 */
interface AuthState {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  session: AuthSession | null;
  error: string | null;
}

const SESSION_KEY = 'web3_auth_session';
const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Auth context value type
 */
interface AuthContextValue {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  session: AuthSession | null;
  error: string | null;
  login: () => Promise<boolean | undefined>;
  logout: (disconnectWallet?: boolean) => void;
  isSessionValid: boolean;
  sessionExpiresAt: number | undefined;
  sessionAddress: string | undefined;
}

/**
 * Create Auth Context
 */
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * AuthProvider Component - Provides shared authentication state
 *
 * Usage:
 * Wrap your app with this provider:
 * ```tsx
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 * ```
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { address, chainId, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { disconnect } = useDisconnect();

  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isAuthenticating: false,
    session: null,
    error: null,
  });

  // Debug: Log whenever authState changes
  useEffect(() => {
    console.log('[useAuth] ==================== AUTH STATE CHANGED ====================');
    console.log('[useAuth] isAuthenticated:', authState.isAuthenticated);
    console.log('[useAuth] isAuthenticating:', authState.isAuthenticating);
    console.log('[useAuth] hasSession:', !!authState.session);
    console.log('[useAuth] session address:', authState.session?.address);
    console.log('[useAuth] error:', authState.error);
    console.log('[useAuth] ===========================================================');
  }, [authState]);

  /**
   * Load session from localStorage
   */
  const loadSession = useCallback((): AuthSession | null => {
    try {
      const storedSession = localStorage.getItem(SESSION_KEY);
      if (!storedSession) return null;

      const session: AuthSession = JSON.parse(storedSession);

      // Check if session is expired
      if (Date.now() > session.expiresAt) {
        localStorage.removeItem(SESSION_KEY);
        return null;
      }

      // Verify session belongs to current wallet
      if (address && session.address.toLowerCase() !== address.toLowerCase()) {
        localStorage.removeItem(SESSION_KEY);
        return null;
      }

      return session;
    } catch (error) {
      console.error('Failed to load session:', error);
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
  }, [address]);

  /**
   * Save session to localStorage
   */
  const saveSession = useCallback((session: AuthSession) => {
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } catch (error) {
      console.error('Failed to save session:', error);
    }
  }, []);

  /**
   * Clear session from localStorage
   */
  const clearSession = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
  }, []);

  /**
   * Create SIWE message
   */
  const createSiweMessage = useCallback((address: string, chainId: number): string => {
    const domain = window.location.host;
    const origin = window.location.origin;
    const now = new Date();
    const expirationTime = new Date(now.getTime() + SESSION_DURATION);

    const siweMessage = new SiweMessage({
      domain,
      address,
      statement: 'Sign in to authenticate your wallet',
      uri: origin,
      version: '1',
      chainId,
      issuedAt: now.toISOString(),
      expirationTime: expirationTime.toISOString(),
    });

    return siweMessage.prepareMessage();
  }, []);

  /**
   * Login - Sign message and create session
   */
  const login = useCallback(async () => {
    console.log('[useAuth] Login called', { address, chainId });

    if (!address || !chainId) {
      console.warn('[useAuth] Login failed: No address or chainId');
      setAuthState(prev => ({
        ...prev,
        error: 'Wallet not connected. Please connect your wallet first.',
      }));
      return;
    }

    console.log('[useAuth] Setting isAuthenticating to true');
    setAuthState(prev => ({ ...prev, isAuthenticating: true, error: null }));

    try {
      // Create SIWE message
      const message = createSiweMessage(address, chainId);
      console.log('[useAuth] SIWE message created:', message);

      // Request signature from wallet
      console.log('[useAuth] Requesting signature from wallet...');
      const signature = await signMessageAsync({
        message,
      });
      console.log('[useAuth] Signature received:', signature);

      // Create session
      const now = Date.now();
      const session: AuthSession = {
        address: address.toLowerCase(),
        chainId,
        signature,
        message,
        issuedAt: now,
        expiresAt: now + SESSION_DURATION,
      };

      // Save session
      console.log('[useAuth] Saving session to localStorage');
      saveSession(session);

      // Verify session was saved
      const savedSession = loadSession();
      console.log('[useAuth] Verified session in localStorage:', !!savedSession);

      // Update state - use functional update to ensure we have latest state
      console.log('[useAuth] Updating state to authenticated');
      setAuthState(prev => {
        console.log('[useAuth] Previous state before update:', prev);
        const newState = {
          isAuthenticated: true,
          isAuthenticating: false,
          session,
          error: null,
        };
        console.log('[useAuth] New state after update:', newState);
        return newState;
      });

      console.log('[useAuth] ✅ Authentication successful:', address);

      // Force a small delay to ensure state propagates
      await new Promise(resolve => setTimeout(resolve, 150));

      console.log('[useAuth] State propagation delay complete');

      // Return success status
      return true;
    } catch (error: unknown) {
      console.error('[useAuth] ❌ Authentication failed:', error);

      // Check if user rejected the request
      const isUserRejection = error instanceof Error && (
        error.message.includes('User rejected') ||
        error.message.includes('User denied') ||
        error.message.includes('User cancelled') ||
        error.message.includes('user rejected')
      );

      const errorMessage = isUserRejection
        ? 'Signature request was cancelled. Please try again when ready.'
        : error instanceof Error ? error.message : 'Authentication failed. Please try again.';

      setAuthState({
        isAuthenticated: false,
        isAuthenticating: false,
        session: null,
        error: errorMessage,
      });

      return false;
    }
  }, [address, chainId, createSiweMessage, signMessageAsync, saveSession, loadSession]);

  /**
   * Logout - Clear session and optionally disconnect wallet
   */
  const logout = useCallback((disconnectWallet = false) => {
    clearSession();
    setAuthState({
      isAuthenticated: false,
      isAuthenticating: false,
      session: null,
      error: null,
    });

    if (disconnectWallet) {
      disconnect();
    }

    console.log('Logged out successfully');
  }, [clearSession, disconnect]);

  /**
   * Check and restore session on mount ONLY
   */
  useEffect(() => {
    if (!isConnected || !address) {
      return;
    }

    // Only try to restore session if not authenticated
    if (!authState.isAuthenticated && !authState.isAuthenticating) {
      const session = loadSession();
      if (session) {
        console.log('[useAuth] Restored session from localStorage on mount');
        setAuthState({
          isAuthenticated: true,
          isAuthenticating: false,
          session,
          error: null,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  /**
   * Clear session when wallet disconnects
   */
  useEffect(() => {
    if (!isConnected && authState.isAuthenticated) {
      console.log('[useAuth] Wallet disconnected - clearing auth state');
      setAuthState({
        isAuthenticated: false,
        isAuthenticating: false,
        session: null,
        error: null,
      });
    }
  }, [isConnected, authState.isAuthenticated]);

  /**
   * Handle account change - require re-authentication
   */
  useEffect(() => {
    console.log('[useAuth] Account change check running...');
    console.log('[useAuth] authState.isAuthenticated:', authState.isAuthenticated);
    console.log('[useAuth] authState.session:', !!authState.session);
    console.log('[useAuth] current address:', address);
    console.log('[useAuth] session address:', authState.session?.address);
    console.log('[useAuth] current chainId:', chainId);
    console.log('[useAuth] session chainId:', authState.session?.chainId);

    if (!authState.isAuthenticated || !authState.session) {
      console.log('[useAuth] Account change check: Not authenticated or no session, skipping');
      return;
    }

    // Check if account changed
    if (address && address.toLowerCase() !== authState.session.address.toLowerCase()) {
      console.log('[useAuth] ❌❌❌ ACCOUNT CHANGED - CLEARING AUTH ❌❌❌');
      console.log('[useAuth] Old:', authState.session.address, 'New:', address);
      clearSession();
      setAuthState({
        isAuthenticated: false,
        isAuthenticating: false,
        session: null,
        error: 'Account changed. Please sign in again.',
      });
      return;
    }

    // Check if chain changed
    if (chainId && chainId !== authState.session.chainId) {
      console.log('[useAuth] ❌❌❌ CHAIN CHANGED - CLEARING AUTH ❌❌❌');
      console.log('[useAuth] Old:', authState.session.chainId, 'New:', chainId);
      clearSession();
      setAuthState({
        isAuthenticated: false,
        isAuthenticating: false,
        session: null,
        error: 'Network changed. Please sign in again.',
      });
      return;
    }

    console.log('[useAuth] Account change check: No changes detected, auth state preserved');
  }, [address, chainId, authState.isAuthenticated, authState.session, clearSession]);

  /**
   * Check session expiry periodically
   */
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.session) return;

    const checkExpiry = () => {
      if (Date.now() > authState.session!.expiresAt) {
        console.log('Session expired');
        logout(false);
      }
    };

    // Check every minute
    const interval = setInterval(checkExpiry, 60000);
    return () => clearInterval(interval);
  }, [authState.isAuthenticated, authState.session, logout]);

  // Create context value
  const value: AuthContextValue = {
    // State
    isAuthenticated: authState.isAuthenticated,
    isAuthenticating: authState.isAuthenticating,
    session: authState.session,
    error: authState.error,

    // Actions
    login,
    logout,

    // Utilities
    isSessionValid: authState.isAuthenticated && authState.session !== null,
    sessionExpiresAt: authState.session?.expiresAt,
    sessionAddress: authState.session?.address,
  };

  console.log('[AuthProvider] ========== PROVIDING VALUES ==========');
  console.log('[AuthProvider] isAuthenticated:', value.isAuthenticated);
  console.log('[AuthProvider] isAuthenticating:', value.isAuthenticating);
  console.log('[AuthProvider] hasSession:', !!value.session);
  console.log('[AuthProvider] error:', value.error);
  console.log('[AuthProvider] =======================================');

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth Hook - Access shared authentication state
 *
 * Must be used within AuthProvider.
 *
 * Usage:
 * ```tsx
 * const { isAuthenticated, login, logout, session } = useAuth();
 *
 * if (!isAuthenticated) {
 *   return <button onClick={login}>Sign In</button>;
 * }
 *
 * return <div>Welcome {session?.address}</div>;
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export default useAuth;
